import { useCallback, useEffect, useState } from "react";
import {
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  TextInput,
  View,
} from "react-native";

import { GText } from "@/components/g-text";
import {
  type ParsedPlace,
  PlacesAutocomplete,
} from "@/components/places-autocomplete";
import { DateTimePicker } from "@/components/ui/date-time-picker";
import { DurationPickerList } from "@/components/ui/duration-picker-list";
import { colors } from "@/constants/colors";

import type { EventFormValues } from "./event-display";

interface SectionEditModalProps {
  onChange: (patch: Partial<EventFormValues>) => void;
  onClose: () => void;
  section: "date" | "description" | "location" | "ticket" | "title" | null;
  values: EventFormValues;
}

export function SectionEditModal({
  onChange,
  onClose,
  section,
  values,
}: SectionEditModalProps) {
  const visible = section !== null;
  const [draft, setDraft] = useState<EventFormValues>(values);

  const resetDraft = useCallback(() => {
    if (visible) setDraft(values);
  }, [visible, values]);

  useEffect(() => {
    resetDraft();
  }, [resetDraft]);

  const patchDraft = (patch: Partial<EventFormValues>) =>
    setDraft((d) => ({ ...d, ...patch }));

  const handleDone = () => {
    onChange(draft);
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <Modal
      animationType="fade"
      onRequestClose={handleDone}
      transparent
      visible={visible}
    >
      <Pressable onPress={handleDone} style={styles.backdrop}>
        <Pressable onPress={() => {}} style={styles.sheet}>
          <ScrollView
            keyboardShouldPersistTaps="handled"
            showsVerticalScrollIndicator={false}
            style={
              Platform.OS === "web" ? { overflow: "visible" as any } : undefined
            }
          >
            <View style={styles.buttonRow}>
              <Pressable onPress={handleCancel}>
                <GText style={styles.cancelText}>✕ cancel</GText>
              </Pressable>
              <GText style={styles.sectionTitle}>edit {section}</GText>
              <Pressable onPress={handleDone}>
                <GText style={styles.doneText}>done ✓</GText>
              </Pressable>
            </View>

            {section === "title" && (
              <TitleSection onChange={patchDraft} values={draft} />
            )}
            {section === "description" && (
              <DescriptionSection onChange={patchDraft} values={draft} />
            )}
            {section === "date" && (
              <DateTimeSection onChange={patchDraft} values={draft} />
            )}
            {section === "location" && (
              <LocationSection onChange={patchDraft} values={draft} />
            )}
            {section === "ticket" && (
              <TicketSection onChange={patchDraft} values={draft} />
            )}
          </ScrollView>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

function DateTimeSection({
  onChange,
  values,
}: {
  onChange: (patch: Partial<EventFormValues>) => void;
  values: EventFormValues;
}) {
  const selectedMinutes = parseInt(values.duration_minutes, 10) || null;

  return (
    <>
      <DateTimePicker
        label="starts at"
        onChange={(v) => onChange({ starts_at: v })}
        placeholder="Select start date & time"
        timezone={values.timezone}
        value={values.starts_at}
      />
      <GText style={styles.label}>lasting</GText>
      <DurationPickerList
        onClear={() => onChange({ duration_minutes: "" })}
        onSelectDuration={(minutes) =>
          onChange({ duration_minutes: String(minutes) })
        }
        optional
        selectedMinutes={selectedMinutes}
      />
    </>
  );
}

function DescriptionSection({
  onChange,
  values,
}: {
  onChange: (patch: Partial<EventFormValues>) => void;
  values: EventFormValues;
}) {
  return (
    <Field
      multiline
      onChangeText={(t) => onChange({ description: t })}
      placeholder="add description"
      value={values.description}
    />
  );
}

function Field({
  label,
  multiline,
  onChangeText,
  placeholder,
  value,
}: {
  label?: string;
  multiline?: boolean;
  onChangeText: (t: string) => void;
  placeholder?: string;
  value: string;
}) {
  return (
    <View style={styles.field}>
      {label && <GText style={styles.label}>{label}</GText>}
      <TextInput
        multiline={multiline}
        onChangeText={onChangeText}
        placeholder={placeholder}
        placeholderTextColor={colors.placeholder}
        style={[styles.input, multiline && styles.inputMultiline]}
        value={value}
      />
    </View>
  );
}

function LocationSection({
  onChange,
  values,
}: {
  onChange: (patch: Partial<EventFormValues>) => void;
  values: EventFormValues;
}) {
  return (
    <>
      {Platform.OS === "web" ? (
        <PlacesAutocomplete
          defaultValue={
            values.location_formatted_address ||
            [
              values.location_name,
              values.location_street,
              values.location_city,
              values.location_state,
            ]
              .filter(Boolean)
              .join(", ") ||
            undefined
          }
          onPlaceSelected={(place: ParsedPlace) => onChange(place)}
        />
      ) : (
        <>
          <Field
            label="street"
            onChangeText={(t) => onChange({ location_street: t })}
            placeholder="123 Main St"
            value={values.location_street}
          />
          <Field
            label="city"
            onChangeText={(t) => onChange({ location_city: t })}
            placeholder="City"
            value={values.location_city}
          />
          <Field
            label="state"
            onChangeText={(t) => onChange({ location_state: t })}
            placeholder="State"
            value={values.location_state}
          />
          <Field
            label="postal code"
            onChangeText={(t) => onChange({ location_postal_code: t })}
            placeholder="12345"
            value={values.location_postal_code}
          />
          <Field
            label="country"
            onChangeText={(t) => onChange({ location_country: t })}
            placeholder="US"
            value={values.location_country}
          />
        </>
      )}
    </>
  );
}

function TicketSection({
  onChange,
  values,
}: {
  onChange: (patch: Partial<EventFormValues>) => void;
  values: EventFormValues;
}) {
  const centsNum = parseInt(values.price_cents, 10) || 0;
  const initial =
    centsNum === 0 ? "" : (centsNum / 100).toFixed(2).replace(/\.00$/, "");
  const [dollarInput, setDollarInput] = useState(initial);

  return (
    <View style={styles.ticketRow}>
      <View style={styles.ticketField}>
        <TextInput
          inputMode="decimal"
          keyboardType="decimal-pad"
          onChangeText={(t) => {
            setDollarInput(t);
            const cents = Math.round((parseFloat(t) || 0) * 100);
            onChange({ price_cents: String(cents) });
          }}
          placeholder="5.00"
          placeholderTextColor={colors.placeholder}
          style={styles.input}
          value={dollarInput}
        />
        <GText style={styles.label}>price</GText>
      </View>
      <View style={styles.ticketField}>
        <TextInput
          keyboardType="number-pad"
          onChangeText={(t) => {
            const cleaned = t.replace(/[^0-9]/g, "");
            onChange({ quantity_total: cleaned });
          }}
          placeholder="7"
          placeholderTextColor={colors.placeholder}
          style={styles.input}
          value={values.quantity_total}
        />
        <GText style={styles.label}>quantity</GText>
      </View>
    </View>
  );
}

function TitleSection({
  onChange,
  values,
}: {
  onChange: (patch: Partial<EventFormValues>) => void;
  values: EventFormValues;
}) {
  return (
    <Field
      onChangeText={(t) => onChange({ title: t })}
      placeholder="title"
      value={values.title}
    />
  );
}

const styles = StyleSheet.create({
  backdrop: {
    alignItems: "center",
    backgroundColor: colors.darkOverlay,
    flex: 1,
    justifyContent: "center",
  },
  buttonRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 8,
  },
  cancelText: {
    color: colors.midGray,
    fontSize: 16,
    fontWeight: "600",
  },
  doneText: {
    color: colors.white,
    fontSize: 16,
    fontWeight: "600",
  },
  field: {
    marginBottom: 16,
  },
  input: {
    backgroundColor: colors.cardBg,
    borderColor: colors.divider,
    borderRadius: 12,
    borderWidth: 1,
    color: colors.white,
    fontSize: 16,
    paddingHorizontal: 16,
    paddingVertical: 14,
  },
  inputMultiline: {
    minHeight: 120,
    textAlignVertical: "top",
  },
  label: {
    color: colors.light,
    fontSize: 13,
    marginBottom: 6,
  },
  sectionTitle: {
    color: colors.light,
    fontSize: 14,
    fontWeight: "700",
    letterSpacing: 1,
    marginBottom: 12,
  },
  sheet: {
    backgroundColor: colors.sheet,
    borderColor: colors.cardBg,
    borderRadius: 20,
    borderWidth: 1,
    marginHorizontal: 24,
    maxHeight: "80%",
    maxWidth: 420,
    overflow: "visible" as any,
    paddingHorizontal: 20,
    paddingVertical: 24,
    width: "100%",
  },
  ticketField: {
    flex: 1,
  },
  ticketRow: {
    flexDirection: "row",
    gap: 12,
  },
});
